import React, { useMemo } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import { formatCurrency } from "@north-beam/nb-common/formatting";
import { useClientDataColumns } from "@pages/mmm/hooks/use-client-data-columns";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";

import { SpendConstraintLabel } from "./form/spend-constraint-label";
import { type DataColumn, type SpendConstraint } from "./types";

export function SimpleSpendConstraintsTable({
  spendConstraints,
}: {
  spendConstraints: SpendConstraint[];
}) {
  const { user } = useUser();
  const { clientDataColumns: dataColumns } = useClientDataColumns();
  const { getSpendConstraintFeature } = useClientSettingData();

  const dataColumnMap = useMemo(() => {
    return dataColumns.reduce((pv, cv) => {
      pv.set(String(cv.id), cv);
      return pv;
    }, new Map<string, DataColumn>());
  }, [dataColumns]);

  const formatSpendConstraintCurrency = (value: number) =>
    formatCurrency(value, {
      code: user.currencies.currentCurrency,
      precision: "exact",
    }).replace(/\..*/, "");

  const getSpendConstraintString = (value: number | null) => {
    if (value === null) {
      return "Default";
    }

    return formatSpendConstraintCurrency(value);
  };

  const renderLabels = () => {
    return spendConstraints.map((spendConstraint, i) => {
      const spendConstraintFeature = getSpendConstraintFeature(spendConstraint);
      const dataColumn = spendConstraintFeature
        ? dataColumnMap.get(spendConstraintFeature.id)
        : undefined;
      return (
        <div
          key={`${spendConstraint.id}:label`}
          className={clsx("h-7 flex items-center pl-3 pr-8", {
            "bg-v2-secondary": dataColumn && i % 2 === 1,
            "bg-mars-2": !dataColumn,
          })}
        >
          {dataColumn ? (
            <SpendConstraintLabel dataColumn={dataColumn} size="sm" />
          ) : (
            <div className="text-xs text-mars flex items-center gap-2">
              <div className="w-[18px] flex items-center justify-center">
                <i className="fa-regular fa-circle-exclamation text-sm"></i>
              </div>
              {spendConstraintFeature?.friendly_name}
            </div>
          )}
        </div>
      );
    });
  };

  const renderBudgetLows = () => {
    return spendConstraints.map((spendConstraint, i) => {
      const spendConstraintFeature = getSpendConstraintFeature(spendConstraint);
      const dataColumn = spendConstraintFeature
        ? dataColumnMap.get(spendConstraintFeature.id)
        : undefined;
      return (
        <div
          key={`${spendConstraint.id}:budgetLow`}
          className={clsx("h-7 flex items-center justify-end px-3", {
            "bg-v2-secondary": dataColumn && i % 2 === 1,
            "bg-mars-2": !dataColumn,
          })}
        >
          {dataColumn && (
            <span className="text-xs font-mono">
              {getSpendConstraintString(spendConstraint.budget_low)}
            </span>
          )}
        </div>
      );
    });
  };

  const renderBudgetHighs = () => {
    return spendConstraints.map((spendConstraint, i) => {
      const spendConstraintFeature = getSpendConstraintFeature(spendConstraint);
      const dataColumn = spendConstraintFeature
        ? dataColumnMap.get(spendConstraintFeature.id)
        : undefined;

      return (
        <div
          key={`${spendConstraint.id}:budgetHigh`}
          className={clsx("h-7 flex items-center justify-end px-3", {
            "bg-v2-secondary": dataColumn && i % 2 === 1,
            "bg-mars-2": !dataColumn,
          })}
        >
          {dataColumn ? (
            <span className="text-xs font-mono">
              {getSpendConstraintString(spendConstraint.budget_high)}
            </span>
          ) : (
            <span className="text-mars text-[9px] absolute">
              Feature no longer available
            </span>
          )}
        </div>
      );
    });
  };

  return (
    <div className="flex -ml-3 -mr-3 -mt-1 -mb-1.5">
      <div className="flex flex-col">
        <div className="h-6"></div>
        {renderLabels()}
      </div>
      <div className="flex flex-col">
        <div className="h-6 flex items-center justify-end px-3">
          <span className="text-xs underline">MIN</span>
        </div>
        {renderBudgetLows()}
      </div>
      <div className="flex flex-col">
        <div className="h-6 flex items-center justify-end px-3">
          <span className="text-xs underline">MAX</span>
        </div>
        {renderBudgetHighs()}
      </div>
    </div>
  );
}
